import * as qs from 'query-string';
import * as _ from 'lodash';
import * as moment from 'moment';
import { useEffect, useState } from 'react';
import { connect, Dispatch } from 'react-redux';
import { IBaseResponse, ISerologyTracking, ISerologyTrackingCounts, SerologyTrackingListType } from '@nkr/models/index';
import { AppActionCreators, IApplicationState, ISerologyTrackingCountsResponse, ISerologyTrackingListResponse, ISerologyTrackingResponse, SerologyTrackingActionCreators } from '@nkr/store/index';
import { toast } from 'react-toastify';
import { IFilterData } from './filter-data';
import { AdminActions } from '@nkr/views/serology-tracking/models';
import Grid from '@nkr/views/serology-tracking/grid';
import Tabs from './tabs';
import Filters from './filters';
import MarkReceivedConfirm from './mark-received-confirm';
import CancelConfirm from './cancel-confirm';
import MarkShippedOut from '@nkr/views/serology-tracking/mark-shipped-out';
import MarkShippedBack from '@nkr/views/serology-tracking/mark-shipped-back';
import MarkResultsSentConfirm from './mark-results-sent-confirm';
import DeleteConfirm from './delete-confirm';

interface IProps { }

interface IStateProps { }

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getCounts: () => Promise<ISerologyTrackingCountsResponse>,
    getByListType: (listType: SerologyTrackingListType) => Promise<ISerologyTrackingListResponse>,
    markReceived: (id: number) => Promise<ISerologyTrackingResponse>,
    markShippedOut: (id: number, trackingNumber: string, shippedDateTime: string) => Promise<ISerologyTrackingResponse>,
    markRemoteShipped: (id: number, trackingNumber: string, shippedDateTime: string) => Promise<ISerologyTrackingResponse>,
    markShippedBack: (id: number, trackingNumber: string, shippedDateTime: string) => Promise<ISerologyTrackingResponse>,
    markCancelled: (id: number) => Promise<IBaseResponse>,
    markResultsSent: (id: number) => Promise<IBaseResponse>,
    updateDeadline: (id: number, deadline: string) => Promise<IBaseResponse>,
    deleteSerologyTracking: (id: number) => Promise<IBaseResponse>,
}

type TProps = IProps & IStateProps & IDispatchProps;

const SerologyTrackingAdmin = (props: TProps) => {
    const [counts, setCounts] = useState<ISerologyTrackingCounts>(null);
    const [list, setList] = useState<ISerologyTracking[]>([]);
    const [listType, setListType] = useState(SerologyTrackingListType.PendShipping);
    const [filters, setFilters] = useState<IFilterData>({ filterBy: 1, centerId: 0 });
    const [deadline, setDeadline] = useState({ id: 0, value: '' });
    const [markShipped, setMarkShipped] = useState<{ show: boolean, data: ISerologyTracking, isRemote: boolean }>({ show: false, data: null, isRemote: false });
    const [markShippedBack, setMarkShippedBack] = useState<{ show: boolean, data: ISerologyTracking }>({ show: false, data: null });
    const [markReceived, setMarkReceived] = useState<{ show: boolean, data: ISerologyTracking }>({ show: false, data: null });
    const [markCancelled, setMarkCancelled] = useState<{ show: boolean, data: ISerologyTracking }>({ show: false, data: null });
    const [markResultsSent, setMarkResultsSent] = useState<{ show: boolean, data: ISerologyTracking }>({ show: false, data: null });
    const [deleteConfirm, setDeleteConfirm] = useState<{ show: boolean, data: ISerologyTracking }>({ show: false, data: null });

    useEffect(() => {
        getData(listType);
    }, []);

    useEffect(() => {
        if (deadline.id > 0) {
            let newList = _.cloneDeep(list).map(x => {
                if (x.id == deadline.id) {
                    x.statusDeadline = moment(deadline.value, 'YYYY-MM-DD').format('MM/DD/YYYY');
                }

                return x;
            });

            setList(newList);
            setDeadline({ id: 0, value: '' });
        }
    }, [deadline]);

    const getData = (listType: SerologyTrackingListType) => {
        let parsed = qs.parse(location.search.length > 0 ? location.search : encodeURI(''), { arrayFormat: 'bracket' });

        let lt = parsed.lt != null ? parseInt(parsed.lt) : listType;
        let fb = parsed.fb != null ? parseInt(parsed.fb) : 1;
        let cid = parsed.cid != null ? parseInt(parsed.cid) : 0;

        let filters = { filterBy: fb, centerId: cid };

        setListType(lt);
        setFilters(filters);
        refreshData(lt);
    };

    const refreshData = (listType: SerologyTrackingListType) => {
        props.showLoading('Getting serology tracking data, one moment please...').then(() => {
            Promise.all([
                props.getCounts(),
                props.getByListType(listType)
            ])
                .then(([countsResponse, listResponse]) => {
                    setCounts(countsResponse.data.results);
                    setList(listResponse.data.results);
                }).finally(() => {
                    props.hideLoading();
                });
        });
    };

    const handleTabChange = (tab: number) => {
        setListType(tab);
        setFilters({ filterBy: 1, centerId: 0 });
        refreshData(tab);
        setUrlParameters(tab, { filterBy: 1, centerId: 0 });
    };

    const handleFilterChange = (filters: IFilterData) => {
        setFilters(filters);
        setUrlParameters(listType, filters);
    };

    const handleAction = (data: ISerologyTracking, action: number) => {
        if (action == AdminActions.MarkShipped) {
            setMarkShipped({
                show: true,
                data,
                isRemote: false
            });
        }

        if (action == AdminActions.MarkReceived) {
            setMarkReceived({
                show: true,
                data
            });
        }

        if (action == AdminActions.MarkCancelled) {
            setMarkCancelled({
                show: true,
                data
            });
        }

        if (action == AdminActions.ShipToRemoteDonor) {
            setMarkShipped({
                show: true,
                data,
                isRemote: true
            });
        }

        if (action == AdminActions.SerologyTestsShippedBack) {
            setMarkShippedBack({
                show: true,
                data
            });
        }

        if (action == AdminActions.ResultsSent) {
            setMarkResultsSent({
                show: true,
                data
            });
        }

        if (action == AdminActions.Delete) {
            setDeleteConfirm({
                show: true,
                data
            });
        }
    };

    const handleUpdateDeadline = (id: number, value: string) => {
        props.showLoading('Updating deadline, one moment please...').then(() => {
            props.updateDeadline(id, value)
                .then(response => {
                    if (response.data.success) {
                        setDeadline({ id, value });
                    }
                })
                .finally(() => {
                    props.hideLoading();
                });
        });
    };

    const handleMarkReceivedSubmit = () => {
        let id = markReceived.data.id;

        setMarkReceived({
            show: false,
            data: null
        });

        props.showLoading('Updating serology, one moment please...').then(() => {
            props.markReceived(id)
                .then(response => {
                    if (response.data.success) {
                        refreshData(listType);
                        toast.success('Serology record updated successfully');
                    } else {
                        toast.error('Unable to update serology record');
                    }
                })
                .finally(() => {
                    props.hideLoading();
                });
        });
    };

    const handleMarkReceivedCancel = () => {
        setMarkReceived({
            show: false,
            data: null
        });
    };

    const handleMarkCancelledSubmit = () => {
        let id = markCancelled.data.id;

        setMarkCancelled({
            show: false,
            data: null
        });

        props.showLoading('Updating serology, one moment please...').then(() => {
            props.markCancelled(id)
                .then(response => {
                    if (response.data.success) {
                        refreshData(listType);
                        toast.success('Serology cancelled successfully');
                    } else {
                        toast.error('Unable to cancel serology');
                    }
                })
                .finally(() => {
                    props.hideLoading();
                });
        });
    };

    const handleMarkCancelledCancel = () => {
        setMarkCancelled({
            show: false,
            data: null
        });
    };

    const handleMarkShippedSubmit = (trackingNumber: string, shippedDate: string) => {
        let id = markShipped.data.id;
        let isRemote = markShipped.isRemote;

        setMarkShipped({
            show: false,
            data: null,
            isRemote: false
        });

        let action = isRemote == false ? props.markShippedOut : props.markRemoteShipped;

        props.showLoading('Updating serology, one moment please...').then(() => {
            action(id, trackingNumber, shippedDate)
                .then(response => {
                    if (response.data.success) {
                        refreshData(listType);
                        toast.success('Serology updated successfully');
                    } else {
                        toast.error('Unable to cancel serology');
                    }
                })
                .finally(() => {
                    props.hideLoading();
                });
        });
    };

    const handleMarkShippedCancel = () => {
        setMarkShipped({
            show: false,
            data: null,
            isRemote: false
        });
    };

    const handleMarkShippedBackSubmit = (trackingNumber: string, shippedDate: string) => {
        let id = markShippedBack.data.id;

        setMarkShippedBack({
            show: false,
            data: null,
        });

        props.showLoading('Updating serology, one moment please...').then(() => {
            props.markShippedBack(id, trackingNumber, shippedDate)
                .then(response => {
                    if (response.data.success) {
                        refreshData(listType);
                        toast.success('Serology updated successfully');
                    } else {
                        toast.error('Unable to cancel serology');
                    }
                })
                .finally(() => {
                    props.hideLoading();
                });
        });
    };

    const handleMarkShippedBackCancel = () => {
        setMarkShippedBack({
            show: false,
            data: null,
        });
    };

    const handleMarkResultsSentSubmit = () => {
        let id = markResultsSent.data.id;

        setMarkResultsSent({
            show: false,
            data: null
        });

        props.showLoading('Updating serology, one moment please...').then(() => {
            props.markResultsSent(id)
                .then(response => {
                    if (response.data.success) {
                        refreshData(listType);
                        toast.success('Serology cancelled successfully');
                    } else {
                        toast.error('Unable to cancel serology');
                    }
                })
                .finally(() => {
                    props.hideLoading();
                });
        });
    };

    const handleMarkResultsSentCancel = () => {
        setMarkResultsSent({
            show: false,
            data: null
        });
    };

    const handleDeleteConfirmSubmit = () => {
        let id = deleteConfirm.data.id;

        setDeleteConfirm({
            show: false,
            data: null
        });

        props.showLoading('Deleting serology, one moment please...').then(() => {
            props.deleteSerologyTracking(id)
                .then(response => {
                    if (response.data.success) {
                        refreshData(listType);
                        toast.success('Serology deleted successfully');
                    } else {
                        toast.error('Unable to delete serology');
                    }
                })
                .finally(() => {
                    props.hideLoading();
                });
        });
    };

    const handleDeleteConfirmCancel = () => {
        setDeleteConfirm({
            show: false,
            data: null
        });
    };

    const setUrlParameters = (tab: number, filters: IFilterData) => {
        let paramList = [];

        paramList.push(encodeURI('lt=' + tab));
        paramList.push(encodeURI('fb=' + filters.filterBy));
        paramList.push(encodeURI('cid=' + filters.centerId));

        let params = paramList.join('&');

        let newUrl = '/admin/serology-tracking?' + params

        window.history.pushState({}, '', newUrl);
    };

    const centerList = _.sortBy(_.uniqBy(list.map(x => {
        if (filters.filterBy == 1) {
            return { id: x.donorCenterId, description: x.donorCenter };
        }

        return { id: x.recipientCenterId, description: x.recipientCenter };
    }), x => x.id), x => x.description);

    const filteredList = list.filter(x => {
        let donorCenterFilter = filters.filterBy == 1 && filters.centerId > 0 ? x.donorCenterId == filters.centerId : true;
        let recipientCenterFilter = filters.filterBy == 2 && filters.centerId > 0 ? x.recipientCenterId == filters.centerId : true;

        return donorCenterFilter && recipientCenterFilter;
    });

    return (
        <>
            <div style={{ fontSize: 28, fontWeight: 500, marginBottom: 10 }}>SEROLOGY TRACKING</div>

            <Tabs
                listType={listType}
                counts={counts}
                onTabChange={handleTabChange} />

            <Filters
                filters={filters}
                centerList={centerList}
                onChange={handleFilterChange}
            />

            <Grid
                data={filteredList}
                centerId={0}
                adminListType={listType}
                isAdmin={true}
                onView={function (id: number): void {
                    throw new Error('Function not implemented.');
                }}
                onAction={handleAction}
                onSerologyTrackingDetails={function (id: number): void {
                    throw new Error('Function not implemented.');
                }}
                onUpdateDeadline={handleUpdateDeadline}
            />

            <MarkReceivedConfirm
                show={markReceived.show}
                data={markReceived.data}
                onConfirm={handleMarkReceivedSubmit}
                onCancel={handleMarkReceivedCancel} />

            <CancelConfirm
                show={markCancelled.show}
                data={markCancelled.data}
                onConfirm={handleMarkCancelledSubmit}
                onCancel={handleMarkCancelledCancel} />

            <MarkShippedOut
                show={markShipped.show}
                data={markShipped.data}
                title={markShipped.isRemote == false ? 'Mark Shipped' : 'Mark Shipped to Remote Donor'}
                isPanel={false}
                onSubmit={handleMarkShippedSubmit}
                onCancel={handleMarkShippedCancel} />

            <MarkShippedBack
                show={markShippedBack.show}
                data={markShippedBack.data}
                onSubmit={handleMarkShippedBackSubmit}
                onCancel={handleMarkShippedBackCancel} />

            <MarkResultsSentConfirm
                show={markResultsSent.show}
                data={markResultsSent.data}
                onConfirm={handleMarkResultsSentSubmit}
                onCancel={handleMarkResultsSentCancel} />

            <DeleteConfirm
                show={deleteConfirm.show}
                data={deleteConfirm.data}
                onConfirm={handleDeleteConfirmSubmit}
                onCancel={handleDeleteConfirmCancel} />
        </>
    );
}

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {
        app: appState.app
    };
};

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getCounts: () => {
            return dispatch(SerologyTrackingActionCreators.getCounts());
        },
        getByListType: (listType) => {
            return dispatch(SerologyTrackingActionCreators.getByListType(listType));
        },
        markReceived: (id) => {
            return dispatch(SerologyTrackingActionCreators.markReceived(id, 0));
        },
        markShippedOut: (id, trackingNumber, shippedDateTime) => {
            return dispatch(SerologyTrackingActionCreators.markShippedOut(id, 0, trackingNumber, shippedDateTime));
        },
        markRemoteShipped: (id, trackingNumber, shippedDateTime) => {
            return dispatch(SerologyTrackingActionCreators.markRemoteShipped(id, 0, trackingNumber, shippedDateTime));
        },
        markShippedBack: (id, trackingNumber, shippedDateTime) => {
            return dispatch(SerologyTrackingActionCreators.markShippedBack(id, 0, trackingNumber, shippedDateTime));
        },
        markCancelled: (id) => {
            return dispatch(SerologyTrackingActionCreators.markCancelled(id));
        },
        markResultsSent: (id) => {
            return dispatch(SerologyTrackingActionCreators.markResultsSent(id));
        },
        updateDeadline: (id, deadline) => {
            return dispatch(SerologyTrackingActionCreators.updateDeadline(id, deadline));
        },
        deleteSerologyTracking: (id) => {
            return dispatch(SerologyTrackingActionCreators.deleteSerologyTracking(id));
        },
    };
};

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps)(SerologyTrackingAdmin);